import type { DocOption } from "building-types/options/optionSet";
import type { OptionOverrides } from "building-types/options/optionTemplateTypes";
import { isExteriorPaletteOptionSet } from "doc/options/paletteOptionSet";
import Toggle from "lib/components/actions/Toggle";
import OptionSelectCircle from "lib/components/builders/communities/plans/OptionSelectCircle";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { SimpleDropDownMenu } from "ui/components/DropdownMenu";
import Link from "ui/components/Link";
import cx from "ui/cx";
import useAppContext from "ui/hooks/useAppContext";
import useDraggableItem from "ui/hooks/useDraggableItem";
import IconEllipsis from "ui/icons/IconEllipsis";
import IconHamburger from "ui/icons/IconHamburger";
import IconLocked from "ui/icons/IconLocked";
import IconTooltip from "ui/icons/IconTooltip";
import Tooltip from "ui/popovers/Tooltip";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column } from "ui/tables/DataTable/types";
import { PlanLibraryBadge } from "./PlanLibraryBadge";
import { extraOptionMsg } from "./PlanLibraryMessages";
import styles from "./PlanLibraryOptionSets.module.scss";
import { PlanLibraryOptionValue } from "./PlanLibraryOptionValue";
import { planLibraryActions } from "./planLibraryReducer";
import { PlanLibraryRuleBadge } from "./PlanLibraryRuleBadge";
import { usePlanLibraryDispatch, usePlanLibrarySelector } from "./planLibraryStore";
import type { PlanLibraryOptionSet } from "./planLibraryTypes";
import {
  planLibraryGetInheritedAndCurrentPrice,
  planLibraryIsOptionOverridden,
  planLibraryIsOptionSetOverridden,
} from "./planLibraryUtils";

interface NonBoolOptionSetTableProps {
  optionSet: PlanLibraryOptionSet;
  draggable: boolean;
  reorder: (src: number, tgt: number) => void;
  index: number;
}

function NonBoolOptionSetTable({ optionSet, draggable, reorder, index }: NonBoolOptionSetTableProps) {
  const { setUserNotification } = useAppContext();
  const dispatch = usePlanLibraryDispatch();

  const { optionSetAffectsEntities, inheritedOptionSet, optionSetOverride, isReadOnly, builder } =
    usePlanLibrarySelector((state) => ({
      optionSetAffectsEntities: !!state.optionSetEntityRelations.optionSetIdToAffectedEntities[optionSet.id],
      inheritedOptionSet: state.inheritedOptionSets[optionSet.id],
      optionSetOverride: state.overrides[optionSet.id],
      isReadOnly: state.isReadOnly,
      builder: state.builder,
    }));

  const { dragRef, dropRef } = useDraggableItem({
    dragItemType: "option-set-table",
    dragItem: reorder,
    index,
  });

  const isExteriorPalette = isExteriorPaletteOptionSet(optionSet);
  const noFloorPlanEffect = !isExteriorPalette && !optionSetAffectsEntities;

  function optionOverflowMenu(option: DocOption): ReactNode[] {
    const optionOverflowMenu = [
      <Link
        key={`${optionSet.id}_${option.id}_edit_details`}
        onClick={(e) => {
          dispatch(planLibraryActions.enableOverrideModal({ optionSet, option }));
          e.stopPropagation();
        }}
      >
        Edit Details
      </Link>,
    ];

    if (optionSet.defaultValue !== option.id) {
      optionOverflowMenu.push(
        <Link
          key={`${optionSet.id}_${option.id}_set_default`}
          onClick={(e) => {
            dispatch(
              planLibraryActions.overrideOptionSet({
                newOptionSet: { ...optionSet, defaultValue: option.id },
                oldOptionSet: optionSet,
              }),
            );
            e.stopPropagation();
          }}
        >
          {isExteriorPalette ? "Set Palette as Base" : "Set Option as Base"}
        </Link>,
      );
    }

    return optionOverflowMenu;
  }

  function getOptionOverrides(optionId: string) {
    return optionSetOverride?.options?.[optionId];
  }

  function getInheritedOptionValue(optionId: string, key: keyof DocOption) {
    return inheritedOptionSet?.options?.find(({ id }) => id === optionId)?.[key];
  }

  const columns: Column<DocOption>[] = useMemo(() => {
    return [
      {
        header: "Option Name",
        bold: true,
        width: 0.4,
        cellClassNameOverrides: { dynamicCell: false },
        cell: ({ row }) => {
          const option = row.original;
          const optionOverrides = optionSetOverride?.options?.[option.id];
          const isDefaultOption = option.id === optionSet.defaultValue;
          const baseOptionBadge = isDefaultOption ? (
            <PlanLibraryBadge singleField isBaseOption={isDefaultOption} />
          ) : null;

          return (
            <div className={styles.tableOptionSetNameWrapper}>
              <PlanLibraryOptionValue
                field="Name"
                isOverridden={optionOverrides?.name !== undefined}
                inheritedValue={getInheritedOptionValue(option.id, "name")}
              >
                <div className={styles.optionNameWrapper}>{option.name}</div>
              </PlanLibraryOptionValue>

              <div className={styles.tableOptionSetBadgeWrapper}>
                {baseOptionBadge}
                <PlanLibraryRuleBadge optionId={option.id} optionSet={optionSet} />
                <PlanLibraryBadge
                  isOverridden={planLibraryIsOptionOverridden(optionOverrides)}
                  onClickBadge={() => {
                    dispatch(planLibraryActions.enableOverrideModal({ optionSet, option }));
                    dispatch(
                      planLibraryActions.setOverridesToDiscard({
                        overridesToDiscard: { options: { [option.id]: optionOverrides as OptionOverrides } },
                        onHandleDiscarding: () => {
                          dispatch(planLibraryActions.discardOptionOverride({ optionSet, optionId: option.id }));
                          setUserNotification({
                            message: `Overrides discarded for ${option.name}`,
                            messageType: "success",
                          });
                        },
                      }),
                    );
                  }}
                />
              </div>
            </div>
          );
        },
      },

      {
        header: "Price",
        width: 0.09,
        cell: ({ row }) => {
          const option = row.original;
          const optionOverrides = getOptionOverrides(option.id);

          const { inheritPrice, currentPrice } = planLibraryGetInheritedAndCurrentPrice(
            builder,
            inheritedOptionSet,
            optionSetOverride,
            option,
          );

          return (
            <PlanLibraryOptionValue
              field="Price"
              isOverridden={optionOverrides?.priceTbd !== undefined || optionOverrides?.price !== undefined}
              inheritedValue={inheritPrice}
            >
              {currentPrice}
            </PlanLibraryOptionValue>
          );
        },
      },

      {
        header: "Builder Option ID",
        cell: ({ row }) => {
          const option = row.original;
          const optionOverrides = getOptionOverrides(option.id);

          return (
            <PlanLibraryOptionValue
              field="Builder Option ID"
              isOverridden={optionOverrides?.builderOptionCode !== undefined}
              inheritedValue={getInheritedOptionValue(option.id, "builderOptionCode")}
            >
              <div style={{ width: "200px" }} className={styles.optionNameWrapper}>
                {option.builderOptionCode}
              </div>
            </PlanLibraryOptionValue>
          );
        },
      },

      {
        header: "Enabled",
        width: 0.1,
        cell: ({ row }) => {
          const option = row.original;
          const optionOverrides = getOptionOverrides(option.id);
          const isDefaultOption = option.id === optionSet.defaultValue;

          return (
            <div
              className={styles.enabledCheckboxWrapper}
              onClick={(event) => {
                if (!isReadOnly && !isDefaultOption) {
                  const options = optionSet.options.map((x) =>
                    x.id === option.id ? { ...x, disabled: !x.disabled } : x,
                  );
                  dispatch(
                    planLibraryActions.overrideOptionSet({
                      newOptionSet: { ...optionSet, options },
                      oldOptionSet: optionSet,
                    }),
                  );
                  event.stopPropagation();
                }
              }}
            >
              {isDefaultOption ? (
                <IconLocked variant="portal" color="stoneLight" size={18} />
              ) : (
                <OptionSelectCircle
                  selected={!option.disabled}
                  highlighted={!!optionOverrides?.disabled}
                  disabled={isReadOnly}
                />
              )}
            </div>
          );
        },
      },
    ];
  }, [optionSet, optionSetOverride, inheritedOptionSet, isReadOnly, builder, dispatch, setUserNotification]);

  return (
    <>
      <div
        style={optionSet.isFilteredBySearch ? { display: "none" } : {}}
        className={styles.optionTableWrapper}
        ref={dropRef}
      >
        <header>
          <div className={styles.lhs}>
            {draggable && (
              <span ref={dragRef} className={styles.optionSetsTableDragHandle}>
                <IconHamburger size={9} />
              </span>
            )}

            {noFloorPlanEffect && (
              <Tooltip.Root delayDuration={0}>
                <Tooltip.Trigger>
                  <div style={{ marginTop: "5px" }}>
                    <IconTooltip color="stoneDark" size={16} />
                  </div>
                </Tooltip.Trigger>
                <Tooltip.Content>
                  <div className={styles.tooltip}>{extraOptionMsg}</div>
                </Tooltip.Content>
              </Tooltip.Root>
            )}

            <PlanLibraryOptionValue
              field="Name"
              isOverridden={optionSetOverride?.name !== undefined}
              inheritedValue={inheritedOptionSet?.name}
            >
              <h3 className={cx(styles.optionSetTitle, draggable ? styles.draggable : "")}>{optionSet.name}</h3>
            </PlanLibraryOptionValue>

            <div className={styles.optionSetBadgeWrapper}>
              <PlanLibraryBadge
                isOverridden={planLibraryIsOptionSetOverridden(optionSetOverride, optionSet)}
                onClickBadge={() => {
                  dispatch(planLibraryActions.enableOverrideModal({ optionSet }));
                  dispatch(
                    planLibraryActions.setOverridesToDiscard({
                      overridesToDiscard: optionSetOverride,
                      onHandleDiscarding: () => {
                        dispatch(planLibraryActions.discardOptionSetOverride({ optionSet }));
                        setUserNotification({
                          message: `Overrides discarded for ${optionSet.name}`,
                          messageType: "success",
                        });
                      },
                    }),
                  );
                }}
              />
            </div>
          </div>

          <div className={styles.rhs}>
            <div className={styles.toggle}>
              <Toggle
                isTrue={!optionSet.disabled}
                onChange={() => {
                  dispatch(
                    planLibraryActions.overrideOptionSet({
                      newOptionSet: { ...optionSet, disabled: !optionSet.disabled },
                      oldOptionSet: optionSet,
                    }),
                  );
                }}
                disabled={isReadOnly}
              />
            </div>

            {!isReadOnly && (
              <div
                className={styles.optionSetDropdownWrapper}
                style={{ visibility: optionSet.disabled ? "hidden" : "visible" }}
              >
                <IconEllipsis
                  size={16}
                  color="stoneDark"
                  onClick={() => dispatch(planLibraryActions.enableOverrideModal({ optionSet }))}
                />
              </div>
            )}
          </div>
        </header>

        {!optionSet.disabled && (
          <ConfigTable
            data={optionSet.options}
            columns={columns}
            draggable={!isReadOnly && optionSet.options.length > 1}
            onRowDrop={(opts) => {
              const options: DocOption[] = opts.map((opt, sortIndex) => ({ ...opt, sortIndex }));
              dispatch(
                planLibraryActions.overrideOptionSet({
                  newOptionSet: { ...optionSet, options },
                  oldOptionSet: optionSet,
                }),
              );
            }}
            onRowClick={(row) => {
              if (!isReadOnly) {
                const option = row.original;
                dispatch(planLibraryActions.enableOverrideModal({ optionSet, option }));
              }
            }}
            overflowMenu={(row) => {
              if (!isReadOnly) {
                const option = row.original;
                return <SimpleDropDownMenu menuItems={optionOverflowMenu(option)} />;
              } else {
                return null;
              }
            }}
          />
        )}
      </div>
    </>
  );
}

export default function PlanLibraryNonBoolOptionSets({ salesCategoryName }: { salesCategoryName: string }) {
  const { isReadOnly, optionSets, searchFor } = usePlanLibrarySelector((state) => ({
    isReadOnly: state.isReadOnly,
    optionSets: state.nonBoolOptionSetsPerSalesCategory[salesCategoryName],
    searchFor: state.searchFor,
  }));
  const dispatch = usePlanLibraryDispatch();

  function reorder(sourceIndex: number, targetIndex: number) {
    if (sourceIndex !== targetIndex) {
      const itemToMove = optionSets[sourceIndex];
      const reordered = [...optionSets];

      reordered.splice(sourceIndex, 1);
      reordered.splice(targetIndex, 0, itemToMove);

      const pairs = reordered.map((oldOptionSet, newSortIndex) => ({
        oldOptionSet,
        newSortIndex,
      }));

      dispatch(
        planLibraryActions.reorderOptionSetsInSalesCategory({
          salesCategoryName,
          sortTuples: pairs,
        }),
      );
    }
  }

  return (
    <>
      {optionSets.map((optionSet, i) => (
        <NonBoolOptionSetTable
          draggable={!isReadOnly && optionSets.length > 1 && !searchFor}
          optionSet={optionSet}
          reorder={reorder}
          index={i}
          key={optionSet.id}
        />
      ))}
    </>
  );
}
